import { GridColumnType } from '@grid'
import { DISPATCH_CARRIER_TITLE } from '@src/configs/dispatch.config'
import dayjs from 'dayjs'

const dateFormatter = (value: string) =>
	value === null || value === undefined || value === ''
		? ''
		: dayjs(value).format('YYYY-MM-DD')

const datetimeFormatter = (value: string) =>
	value === null || value === undefined || value === ''
		? ''
		: dayjs(value).format('YYYY-MM-DD HH:mm')

/**
 * 배송 관리 = 출고 대장 목록 (`/main/shipping`). 읽기 전용. **행 하나가 배송 건(박스) 하나**다.
 * 열 순서는 엑셀 월 시트를 따른다(DISPATCH_PLAN §3.6) — 엑셀 내보내기가 옛 파일과 같게.
 * 종류 · 상태 · 송장 · 금액 · 주문번호는 페이지가 `renderCell` 로 그린다.
 */
export const dispatchColumns: GridColumnType = {
	initRow: {},
	fieldToFocus: 'partyName',
	card: {
		title: 'orderId',
		status: 'status',
		subtitle: 'partyName',
		meta: ['orderedAt', 'itemSummary', 'carrier', 'trackingNumber'],
		sections: [
			{
				title: '주문',
				fields: [
					'orderedAt',
					'kind',
					'partyName',
					'itemSummary',
					'serials',
					'totalAmount',
					'paidAt',
				],
			},
			{
				title: '수취인',
				fields: ['receiverName', 'receiverPhone', 'address1', 'countryIso'],
			},
			{ title: '박스', fields: ['boxCode', 'weightKg', 'memo'] },
			{
				title: '추적',
				fields: [
					'carrier',
					'trackingNumber',
					'shippedAt',
					'lastStatusDesc',
					'deliveredAt',
				],
			},
		],
	},
	columns: [
		{
			field: 'orderedAt',
			headerName: '주문일',
			headerAlign: 'center',
			align: 'center',
			width: 110,
			editable: false,
			valueFormatter: dateFormatter,
		},
		{
			field: 'kind',
			headerName: '종류',
			headerAlign: 'center',
			align: 'center',
			width: 110,
			editable: false,
		},
		{
			// 칩 하나다(오더 관리와 같은 사다리 — 환불·취소도 상태 값이다)
			field: 'status',
			headerName: '상태',
			headerAlign: 'center',
			align: 'center',
			width: 110,
			editable: false,
		},
		{
			field: 'partyName',
			headerName: '거래처 / 고객',
			headerAlign: 'center',
			width: 160,
			editable: false,
		},
		{
			field: 'itemSummary',
			headerName: '품목',
			headerAlign: 'center',
			width: 180,
			editable: false,
		},
		{
			field: 'serials',
			headerName: 'S/N',
			headerAlign: 'center',
			width: 160,
			editable: false,
		},
		{
			field: 'totalAmount',
			headerName: '금액',
			headerAlign: 'center',
			align: 'right',
			width: 120,
			editable: false,
		},
		{
			field: 'paidAt',
			headerName: '입금일',
			headerAlign: 'center',
			align: 'center',
			width: 110,
			editable: false,
			valueFormatter: dateFormatter,
		},
		{
			field: 'shippedAt',
			headerName: '발송일',
			headerAlign: 'center',
			align: 'center',
			width: 110,
			editable: false,
			valueFormatter: dateFormatter,
		},
		{
			field: 'countryIso',
			headerName: '국가',
			headerAlign: 'center',
			align: 'center',
			width: 70,
			editable: false,
		},
		{
			field: 'carrier',
			headerName: '배송사',
			headerAlign: 'center',
			align: 'center',
			width: 90,
			editable: false,
			valueFormatter: (value: string) =>
				value === '' || value === null || value === undefined
					? ''
					: DISPATCH_CARRIER_TITLE[value] ?? value,
		},
		{
			field: 'trackingNumber',
			headerName: '송장',
			headerAlign: 'center',
			width: 160,
			editable: false,
		},
		{
			field: 'receiverName',
			headerName: '수취인',
			headerAlign: 'center',
			width: 140,
			editable: false,
		},
		{
			field: 'receiverPhone',
			headerName: '전화',
			headerAlign: 'center',
			width: 130,
			editable: false,
		},
		{
			field: 'address1',
			headerName: '주소',
			headerAlign: 'center',
			width: 240,
			editable: false,
		},
		{
			field: 'boxCode',
			headerName: '박스',
			headerAlign: 'center',
			align: 'center',
			width: 70,
			editable: false,
		},
		{
			field: 'weightKg',
			headerName: '무게(kg)',
			headerAlign: 'center',
			align: 'right',
			width: 90,
			editable: false,
			valueFormatter: (value: number) => (value ? value : ''),
		},
		{
			field: 'lastStatusDesc',
			headerName: '최근 상태',
			headerAlign: 'center',
			width: 200,
			editable: false,
		},
		{
			field: 'deliveredAt',
			headerName: '배송 완료',
			headerAlign: 'center',
			width: 140,
			editable: false,
			valueFormatter: datetimeFormatter,
		},
		{
			field: 'memo',
			headerName: '비고',
			headerAlign: 'center',
			width: 200,
			editable: false,
		},
		{
			field: 'orderId',
			headerName: '주문번호',
			headerAlign: 'center',
			width: 220,
			editable: false,
		},
	],
}
